import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import API from '../../../api/axiosInstance';
import OnsiteAudioRecorder from './OnsiteAudioRecorder';
import OnsiteSignatureField from './OnsiteSignatureField';
import CaddieSelectionList from './CaddieSelectionList';
import OnsiteCaddieSearch from './OnsiteCaddieSearch';
import { Field, FieldError, Section, HeroBanner, GOLD, RED } from './OnsiteFormPrimitives';
import OnsiteReviewPage from './OnsiteReviewPage';
import useOnsiteValidation from './useOnsiteValidation';
import logo from '../../../images/logo.png';
import '../../css/OnsiteRegistrationForm.css';
import { GoldLoader } from '../../shared/Loaders';
import { isPossiblePhoneNumber } from 'libphonenumber-js';
import OnsitePhoneField from './OnsitePhoneField';
import { PHONE_ERROR, readOnsiteMobile, getMobileError, buildMobilePatch } from './onsitePhone';
import { getAuthItem } from '../../../utils/authStorage';

// Used for react-select JS-based styles only (cannot use CSS for those)
const GREEN = '#00573C';

// PUT body field for caddies picked here but not yet linked to the athlete. Omitted when there are none.
const addedCaddiesBody = (knownCaddies, linkedIds) => {
  const added = knownCaddies.filter(c => !linkedIds.has(c._id));
  return added.length ? { added_caddies: added } : {};
};

const OnsiteRegistrationForm = () => {
  const [searchParams] = useSearchParams();
  const id       = searchParams.get('id');
  const isCaddie = searchParams.get('type') === 'caddie';
  // const isKiosk  = searchParams.get('kiosk') === 'true'; // Kiosk mode on hold
  const LABEL    = isCaddie ? 'Caddie' : 'Participant';

  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');
  // Onsite V2 (IGFEMS-634): 'edit' = Page 1 form, 'review' = Page 2 check & validate, 'done' = success screen.
  const [step,       setStep]       = useState('edit');
  const [sigError,           setSigError]           = useState(false);
  const [firstNameError,     setFirstNameError]     = useState(false);
  const [lastNameError,      setLastNameError]      = useState(false);
  const [accommodationError, setAccommodationError] = useState(false);
  const [hotelNameError,     setHotelNameError]     = useState(false);
  const [regulationsError,   setRegulationsError]   = useState(false);
  const [mobileNumberError,  setMobileNumberError]  = useState('');
  const [registeredMobileError, setRegisteredMobileError] = useState('');
  // const [countdown, setCountdown] = useState(15); // Kiosk mode on hold
  const audioBlobRef    = useRef(null);
  // The number as saved, to tell an edit from an untouched prefill.
  const savedMobileRef  = useRef({ number: '', dial: '+1', select: '' });
  // Caddies already saved on the athlete, to tell them from ones picked in this session.
  const linkedCaddieIdsRef = useRef(new Set());
  const tapCount        = useRef(0);
  const tapTimer        = useRef(null);
  const hadSigOnLoadRef = useRef(false);
  const validation      = useOnsiteValidation(id);
  const { busy: validationBusy, initFromPersonData, status: validationStatus } = validation;

  // Page 2 follows the server: if another window validated or sent back first (409 refetch), leave Page 2.
  useEffect(() => {
    if (step !== 'review' || !validationStatus || validationStatus === 'Submitted') return;
    setStep(validationStatus === 'Validated' ? 'done' : 'edit');
  }, [step, validationStatus]);

  useEffect(() => {
    const handler = (e) => {
      if (audioBlobRef.current || submitting || validationBusy) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [submitting, validationBusy]);

  const [knownCaddies, setKnownCaddies] = useState([]);
  const [selectedCaddieId, setSelectedCaddieId] = useState(null);

  const [formData, setFormData] = useState({
    firstName: '', lastName: '', scoreboardName: '', height: '',
    staysAtHotel: false, staysAtOlympicVillage: false,
    onsiteHotelName: '', hasAdditionalCaddie: false, additionalCaddyFirstName: '', additionalCaddyLastName: '', mobileDialCode: '+1', mobileNumber: '',
    registeredMobileDial: '+1', registeredMobile: '',
    pronunciationAudioUrl: '', pronunciationComplete: false, pronunciationNotes: '',
    regulationsAcknowledged: false, signatureDataUrl: '',
  });

  useEffect(() => {
    // Staff-only screen: participant (user) or org tokens are sent to the admin login.
    // getAuthItem = this tab's login (an admin "login as user" tab holds a user token in sessionStorage).
    if (!getAuthItem('token') || getAuthItem('role') !== 'admin') {
      window.location.replace('/admin-login');
    }
  }, []);

  // Kiosk mode on hold — countdown auto-redirect and logo tap disabled
  // useEffect(() => {
  //   if (!submitted || !isKiosk) return;
  //   const interval = setInterval(() => {
  //     setCountdown(prev => {
  //       if (prev <= 1) { clearInterval(interval); navigate('/onsite-kiosk'); return 0; }
  //       return prev - 1;
  //     });
  //   }, 1000);
  //   return () => clearInterval(interval);
  // }, [submitted, isKiosk, navigate]);

  // const handleLogoTap = () => {
  //   tapCount.current += 1;
  //   clearTimeout(tapTimer.current);
  //   tapTimer.current = setTimeout(() => { tapCount.current = 0; }, 2000);
  //   if (tapCount.current >= 3) { tapCount.current = 0; navigate('/onsite-kiosk'); }
  // };

  const handleCornerTap = () => {
    tapCount.current += 1;
    clearTimeout(tapTimer.current);
    tapTimer.current = setTimeout(() => { tapCount.current = 0; }, 2000);
    if (tapCount.current >= 3) { tapCount.current = 0; window.close(); }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!id) { setError('Invalid participant ID'); setLoading(false); return; }
      try {
        const pRes = await API.get(`/ems/participants/${id}`);
        const pd   = pRes.data?.person_data || {};
        const person = pRes.data?.person_id || {};
        // Same order as Registration: dataGrid.knownCaddies, then the root copy
        // (used when dataGrid holds Guest/Travel rows).
        const knownCaddiesSrc = pd.dataGrid?.knownCaddies?.length
          ? pd.dataGrid.knownCaddies
          : (Array.isArray(pd.knownCaddies) ? pd.knownCaddies : []);
        setKnownCaddies(knownCaddiesSrc);
        linkedCaddieIdsRef.current = new Set(knownCaddiesSrc.map(c => c._id));
        const savedPresent = Array.isArray(pd.presentCaddies) ? pd.presentCaddies : [];
        setSelectedCaddieId(savedPresent[0]?._id || null);
        const savedMobile = readOnsiteMobile(pd);
        savedMobileRef.current = savedMobile;

        // DB stores additionalCaddies as [{ firstName, lastName }]. Derive UI checkbox state
        // from array content (not length) — empty object in array must not pre-tick checkbox.
        const additionalCaddiesArr   = Array.isArray(pd.additionalCaddies) ? pd.additionalCaddies : [];
        const additionalCaddyEntry   = additionalCaddiesArr[0] || {};
        const hasAdditionalCaddie    = !!(additionalCaddyEntry.firstName || additionalCaddyEntry.lastName);

        setFormData(prev => ({
          ...prev,
          firstName:              pd.givenFirstName      || person.name  || '',
          lastName:               pd.familyLastName      || person.lname || '',
          scoreboardName:         pd.scoreboardName      || '',
          height:                 pd.height              || '',
          staysAtHotel:           pd.staysAtHotel          ?? (!!pd.onsiteHotelName),
          staysAtOlympicVillage:  pd.staysAtOlympicVillage ?? false,
          onsiteHotelName:        pd.onsiteHotelName     || '',
          hasAdditionalCaddie,
          additionalCaddyFirstName: additionalCaddyEntry.firstName || '',
          additionalCaddyLastName:  additionalCaddyEntry.lastName  || '',
          mobileDialCode:         pd.mobileDialCode      || '+1',
          mobileNumber:           pd.mobileNumber        || '',
          registeredMobileDial:   savedMobile.dial,
          registeredMobile:       savedMobile.number,
          pronunciationAudioUrl:  pd.pronunciationAudioUrl  || '',
          pronunciationComplete:  pd.pronunciationComplete  || false,
          pronunciationNotes:     pd.pronunciationNotes  || '',
          regulationsAcknowledged: pd.regulationsAcknowledged || false,
          signatureDataUrl:       pd.signatureDataUrl    || '',
        }));
        hadSigOnLoadRef.current = !!(pd.signatureDataUrl?.startsWith('data:'));
        initFromPersonData(pd);
        // Already submitted but not yet checked by staff → reopen straight on Page 2.
        if (pd.onsiteValidation?.status === 'Submitted') setStep('review');
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
          window.location.replace('/admin-login');
          return;
        }
        setError(`Failed to load: ${err.response?.data?.message || err.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, initFromPersonData]);

  const setField = useCallback((k, v) => setFormData(prev => ({ ...prev, [k]: v })), []);

  const handleSelectCaddie = useCallback((id) => {
    setSelectedCaddieId(prev => prev === id ? null : id);
  }, []);

  // A caddie found or added in the search joins the list and is marked present today.
  const handlePickCaddie = useCallback(({ _id, name, lname, email }) => {
    setKnownCaddies(prev => prev.some(c => c._id === _id) ? prev : [...prev, { _id, name, lname, email }]);
    setSelectedCaddieId(_id);
  }, []);

  const padReadOnly = hadSigOnLoadRef.current;

  const handleSignatureChange = useCallback((dataUrl) => {
    setField('signatureDataUrl', dataUrl);
    if (dataUrl) setSigError(false);
  }, [setField]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setField(name, type === 'checkbox' ? checked : value);
  };

  const handleSubmit = async () => {
    const noFirstName     = !formData.firstName.trim();
    const noLastName      = !formData.lastName.trim();
    const noAccommodation = !formData.staysAtHotel && !formData.staysAtOlympicVillage;
    const noHotelName     = formData.staysAtHotel && !formData.onsiteHotelName.trim();
    // Phone is also checked on blur, but a tablet user can tap SUBMIT straight from the keyboard.
    const badPhone        = !!formData.mobileNumber && !isPossiblePhoneNumber(formData.mobileDialCode + formData.mobileNumber);
    const mobileError     = getMobileError(savedMobileRef.current, formData.registeredMobileDial, formData.registeredMobile);
    const noSig           = !padReadOnly && !formData.signatureDataUrl;
    const noRegs          = !formData.regulationsAcknowledged;

    setFirstNameError(noFirstName);
    setLastNameError(noLastName);
    setAccommodationError(noAccommodation);
    setHotelNameError(noHotelName);
    if (badPhone) setMobileNumberError(PHONE_ERROR);
    setRegisteredMobileError(mobileError);
    setSigError(noSig);
    setRegulationsError(noRegs);

    let firstErrorId = null;
    if (noFirstName || noLastName || noAccommodation || noHotelName || badPhone || mobileError) firstErrorId = 'basic';
    else if (noSig || noRegs) firstErrorId = 'signature';

    if (firstErrorId) {
      document.getElementById(firstErrorId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    tapCount.current = 0;
    setSubmitting(true);
    setError('');
    try {
      let audioUrl = formData.pronunciationAudioUrl;
      if (audioBlobRef.current) {
        if (formData.pronunciationAudioUrl) {
          try { await API.delete('/ems/uploads/audio', { data: { url: formData.pronunciationAudioUrl } }); } catch { /* ignore */ }
        }
        const blob = audioBlobRef.current;
        const ext  = blob.type.includes('mp4') ? 'mp4' : blob.type.includes('wav') ? 'wav' : 'webm';
        const file = new File([blob], `pronunciation-${Date.now()}.${ext}`, { type: blob.type });
        const fd   = new FormData();
        fd.append('file', file);
        fd.append('participantId', id);
        if (formData.firstName) fd.append('firstName', formData.firstName);
        if (formData.lastName)  fd.append('lastName',  formData.lastName);
        const res = await API.post('/ems/uploads/audio', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        audioUrl = res.data.url;
        audioBlobRef.current = null;
        // Keep the uploaded URL in state: after "Back to edit" a second SUBMIT must not send ''.
        setFormData(prev => ({ ...prev, pronunciationAudioUrl: audioUrl, pronunciationComplete: !!audioUrl }));
      }

      // Reset the status BEFORE saving: if the save then fails, a Validated record can't end up
      // holding edited data nobody checked (worst case: Submitted with the old data).
      const { ok, message } = await validation.submit();
      if (!ok) {
        setError(`Could not start the check (${message}). Nothing was saved. Please press SUBMIT again.`);
        return;
      }

      const mobilePatch = buildMobilePatch(savedMobileRef.current, formData.registeredMobileDial, formData.registeredMobile);
      await API.put(`/ems/participants/${id}`, {
        person_data: {
          givenFirstName:          formData.firstName,
          familyLastName:          formData.lastName,
          scoreboardName:          formData.scoreboardName,
          height:                  formData.height,
          staysAtHotel:            formData.staysAtHotel,
          staysAtOlympicVillage:   formData.staysAtOlympicVillage,
          onsiteHotelName:         formData.onsiteHotelName,
          // Store as array for scalability. Empty array when unchecked or names blank — no boolean saved to DB.
          additionalCaddies:   formData.hasAdditionalCaddie && (formData.additionalCaddyFirstName || formData.additionalCaddyLastName)
                                 ? [{ firstName: formData.additionalCaddyFirstName, lastName: formData.additionalCaddyLastName }]
                                 : [],
          // Caddie physically present today. At most one — single-select radio UI.
          presentCaddies:      knownCaddies.filter(c => c._id === selectedCaddieId),
          mobileDialCode:          formData.mobileDialCode,
          mobileNumber:            formData.mobileNumber,
          pronunciationAudioUrl:   audioUrl,
          pronunciationComplete:   !!audioUrl,
          pronunciationNotes:      formData.pronunciationNotes,
          regulationsAcknowledged: formData.regulationsAcknowledged,
          signatureDataUrl:        formData.signatureDataUrl,
          ...mobilePatch,
        },
        ...addedCaddiesBody(knownCaddies, linkedCaddieIdsRef.current),
      });
      linkedCaddieIdsRef.current = new Set(knownCaddies.map(c => c._id));
      if (mobilePatch.container1) {
        savedMobileRef.current = {
          // What the field shows, so a second SUBMIT sees no change even if symbols were stripped.
          number: formData.registeredMobile.trim(),
          dial: formData.registeredMobileDial,
          select: mobilePatch.container1.select,
        };
      }
      // Signature is now saved — keep it locked if staff send the participant back (no re-sign).
      hadSigOnLoadRef.current = !!formData.signatureDataUrl;

      setStep('review');
      window.scrollTo(0, 0);
    } catch (err) {
      setError(`Save failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUrl = useCallback(async () => {
    setField('pronunciationAudioUrl', '');
    try {
      await API.put(`/ems/participants/${id}`, {
        person_data: {
          givenFirstName:          formData.firstName,
          familyLastName:          formData.lastName,
          scoreboardName:          formData.scoreboardName,
          height:                  formData.height,
          staysAtHotel:            formData.staysAtHotel,
          staysAtOlympicVillage:   formData.staysAtOlympicVillage,
          onsiteHotelName:         formData.onsiteHotelName,
          // Same array mapping as handleSubmit — keeps DB consistent on audio delete.
          additionalCaddies:   formData.hasAdditionalCaddie && (formData.additionalCaddyFirstName || formData.additionalCaddyLastName)
                                 ? [{ firstName: formData.additionalCaddyFirstName, lastName: formData.additionalCaddyLastName }]
                                 : [],
          presentCaddies:      knownCaddies.filter(c => c._id === selectedCaddieId),
          mobileDialCode:          formData.mobileDialCode,
          mobileNumber:            formData.mobileNumber,
          pronunciationAudioUrl:   '',
          pronunciationComplete:   false,
          pronunciationNotes:      formData.pronunciationNotes,
          regulationsAcknowledged: formData.regulationsAcknowledged,
          signatureDataUrl:        formData.signatureDataUrl,
        },
        // presentCaddies above may name a caddie picked this session, so link them in the same save.
        ...addedCaddiesBody(knownCaddies, linkedCaddieIdsRef.current),
      });
      linkedCaddieIdsRef.current = new Set(knownCaddies.map(c => c._id));
    } catch { /* silent — S3 already gone, DB clear is best-effort */ }
  }, [id, formData, setField, knownCaddies, selectedCaddieId]);


  if (loading) return (
    <div className="onsite-form-overlay">
      <GoldLoader label="Loading..." variant="page" />
    </div>
  );

  if (submitting) return (
    <div className="onsite-form-overlay">
      <div className="onsite-form-overlay__spinner-wrap">
        <Spinner animation="border" style={{ color: GOLD, width: '36px', height: '36px' }} />
      </div>
      <div style={{ textAlign: 'center' }}>
        <div className="onsite-form-overlay__title">Saving Registration…</div>
        <div className="onsite-form-overlay__caption">Please do not close or go back from this page.</div>
      </div>
    </div>
  );

  const handleBackToEdit = async () => {
    if ((await validation.sendBack()).ok) { setStep('edit'); window.scrollTo(0, 0); }
  };

  const handleValidate = async () => {
    if ((await validation.validate()).ok) setStep('done');
  };

  if (step === 'review') return (
    <OnsiteReviewPage
      formData={formData}
      label={LABEL}
      isCaddie={isCaddie}
      presentCaddie={knownCaddies.find(c => c._id === selectedCaddieId) || null}
      adminName={getAuthItem('username') || ''}
      logo={logo}
      busy={validation.busy}
      error={validation.error}
      onBack={handleBackToEdit}
      onValidate={handleValidate}
    />
  );

  if (step === 'done') return (
    <div className="onsite-form-page">
      <div className="onsite-form-success">
        <div className="onsite-form-success__inner">
          <div className="onsite-form-success__icon">
            <i className="fa-solid fa-check" style={{ fontSize: '36px', color: '#fff' }} />
          </div>
          <h2 className="onsite-form-success__title">{LABEL} Registration Validated</h2>
          <p className="onsite-form-success__sub">
            {formData.firstName} {formData.lastName} has been registered
            {validation.validatedByName ? <> and validated by <strong>{validation.validatedByName}</strong></> : ' and validated'}.
          </p>
        </div>
        <div onClick={handleCornerTap} style={{ position: 'fixed', bottom: 0, left: 0, width: '160px', height: '160px' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.006)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'} />
      </div>
    </div>
  );

  return (
    <div className="onsite-form-page">
      <div className="onsite-form-body">

        <HeroBanner firstName={formData.firstName} lastName={formData.lastName} logo={logo} />

        {validation.status === 'Returned' && (
          <div className="onsite-form-notice" role="status">
            <i className="fa-solid fa-rotate-left" style={{ marginRight: '8px' }} />
            Changes requested. Please update the details and press SUBMIT again.
          </div>
        )}
        {validation.status === 'Validated' && (
          <div className="onsite-form-notice" role="status">
            <i className="fa-solid fa-circle-check" style={{ marginRight: '8px' }} />
            Already validated{validation.validatedByName ? ` by ${validation.validatedByName}` : ''}
            {validation.validatedAt ? ` on ${new Date(validation.validatedAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}` : ''}.
            Submitting again will need a new staff validation.
          </div>
        )}

        {error && (
          <div className="onsite-form-error" role="alert">
            <i className="fa-solid fa-circle-exclamation" style={{ marginRight: '8px' }} />{error}
          </div>
        )}

        {/* BASIC DETAILS */}
        <Section id="basic" icon="fa-user" title="Details" subtitle={`Confirm ${LABEL.toLowerCase()} information`} accent={GOLD}>
          <div className="onsite-form-grid">
            <Field label="First Name" required>
              <input className="onsite-form__input" name="firstName" value={formData.firstName} onChange={(e) => { handleChange(e); if (e.target.value.trim()) setFirstNameError(false); }} placeholder="Enter first name" maxLength={20} />
              {firstNameError && <FieldError>First name is required.</FieldError>}
            </Field>
            <Field label="Last Name" required>
              <input className="onsite-form__input" name="lastName" value={formData.lastName} onChange={(e) => { handleChange(e); if (e.target.value.trim()) setLastNameError(false); }} placeholder="Enter last name" maxLength={20} />
              {lastNameError && <FieldError>Last name is required.</FieldError>}
            </Field>
            <Field label="Scoreboard Name">
              <input className="onsite-form__input" name="scoreboardName" value={formData.scoreboardName} onChange={handleChange} placeholder="Name displayed on scoreboard" maxLength={20} />
            </Field>
            <Field label="Height (cm)">
              <input className="onsite-form__input" name="height" value={formData.height} onChange={(e) => setField('height', e.target.value.replace(/\D/g, ''))} placeholder="e.g. 180" inputMode="numeric" />
            </Field>
            <Field label="Accommodation" required>
              <div className="onsite-form-segment">
                <button
                  type="button"
                  className={`onsite-form-segment__option${formData.staysAtHotel && !formData.staysAtOlympicVillage ? ' onsite-form-segment__option--active' : ''}`}
                  aria-pressed={formData.staysAtHotel && !formData.staysAtOlympicVillage}
                  onClick={() => { setFormData(prev => ({ ...prev, staysAtHotel: true, staysAtOlympicVillage: false })); setAccommodationError(false); }}
                >
                  <i className="fa-solid fa-hotel" /> Hotel
                </button>
                <button
                  type="button"
                  className={`onsite-form-segment__option${!formData.staysAtHotel && formData.staysAtOlympicVillage ? ' onsite-form-segment__option--active' : ''}`}
                  aria-pressed={!formData.staysAtHotel && formData.staysAtOlympicVillage}
                  onClick={() => { setFormData(prev => ({ ...prev, staysAtHotel: false, staysAtOlympicVillage: true, onsiteHotelName: '' })); setAccommodationError(false); setHotelNameError(false); }}
                >
                  <i className="fa-solid fa-city" /> Olympic Village
                </button>
                <button
                  type="button"
                  className={`onsite-form-segment__option${formData.staysAtHotel && formData.staysAtOlympicVillage ? ' onsite-form-segment__option--active' : ''}`}
                  aria-pressed={formData.staysAtHotel && formData.staysAtOlympicVillage}
                  onClick={() => { setFormData(prev => ({ ...prev, staysAtHotel: true, staysAtOlympicVillage: true })); setAccommodationError(false); }}
                >
                  <i className="fa-solid fa-layer-group" /> Both
                </button>
              </div>
              {accommodationError && <FieldError>Please select an accommodation option.</FieldError>}
            </Field>
            {formData.staysAtHotel && (
              <div>
                <label className="onsite-form-field__label">Hotel Name<span className="onsite-form-field__required">*</span></label>
                <input
                  className="onsite-form__input"
                  name="onsiteHotelName"
                  value={formData.onsiteHotelName}
                  onChange={(e) => { handleChange(e); if (e.target.value.trim()) setHotelNameError(false); }}
                  placeholder="e.g. Marriott Downtown"
                />
                {hotelNameError && <FieldError>Hotel name is required.</FieldError>}
                <p className="onsite-form-field__hint">Please let us know where we can reach you</p>
              </div>
            )}
            {!isCaddie && (
              <div style={{ gridColumn: '1 / -1' }}>
                <CaddieSelectionList
                  caddies={knownCaddies}
                  selectedCaddieId={selectedCaddieId}
                  onSelectCaddie={handleSelectCaddie}
                />
                <OnsiteCaddieSearch onPick={handlePickCaddie} />
              </div>
            )}
            {/* DISABLED — additional caddie section hidden; re-enable when needed
            {!isCaddie && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontFamily: '"Poppins", sans-serif', fontSize: '14px', color: '#5c4518', fontWeight: 500 }}>
                  <input
                    type="checkbox"
                    checked={formData.hasAdditionalCaddie}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      hasAdditionalCaddie: e.target.checked,
                      ...(e.target.checked ? {} : { additionalCaddyFirstName: '', additionalCaddyLastName: '' }),
                    }))}
                    style={{ width: '18px', height: '18px', accentColor: '#5c4518', cursor: 'pointer' }}
                  />
                  Do you have an additional caddie to add?
                </label>
              </div>
            )}
            {!isCaddie && formData.hasAdditionalCaddie && (
              <Field label="Additional Caddie First Name">
                <input className="onsite-form__input" value={formData.additionalCaddyFirstName} onChange={(e) => setFormData(prev => ({ ...prev, additionalCaddyFirstName: e.target.value }))} placeholder="First name…" />
              </Field>
            )}
            {!isCaddie && formData.hasAdditionalCaddie && (
              <Field label="Additional Caddie Last Name">
                <input className="onsite-form__input" value={formData.additionalCaddyLastName} onChange={(e) => setFormData(prev => ({ ...prev, additionalCaddyLastName: e.target.value }))} placeholder="Last name…" />
              </Field>
            )}
            */}
            <div style={{ gridColumn: '1' }}>
              <label className="onsite-form-field__label">Mobile Number</label>
              <OnsitePhoneField
                name="registeredMobile"
                placeholder="Enter mobile number"
                dial={formData.registeredMobileDial}
                number={formData.registeredMobile}
                error={registeredMobileError}
                onDialChange={(dial) => {
                  setField('registeredMobileDial', dial);
                  setRegisteredMobileError(getMobileError(savedMobileRef.current, dial, formData.registeredMobile));
                }}
                onNumberChange={(number) => {
                  setField('registeredMobile', number);
                  if (registeredMobileError) setRegisteredMobileError('');
                }}
                onBlur={() => setRegisteredMobileError(
                  getMobileError(savedMobileRef.current, formData.registeredMobileDial, formData.registeredMobile)
                )}
              />
            </div>
            <Field label="Additional Number During Tournament">
              <OnsitePhoneField
                name="mobileNumber"
                placeholder="Enter phone number"
                dial={formData.mobileDialCode}
                number={formData.mobileNumber}
                error={mobileNumberError}
                onDialChange={(dial) => {
                  setField('mobileDialCode', dial);
                  if (formData.mobileNumber) {
                    setMobileNumberError(
                      !isPossiblePhoneNumber(dial + formData.mobileNumber) ? PHONE_ERROR : ''
                    );
                  }
                }}
                onNumberChange={(number) => {
                  setField('mobileNumber', number);
                  if (mobileNumberError) setMobileNumberError('');
                }}
                onBlur={(e) => {
                  const val = e.target.value;
                  if (val && !isPossiblePhoneNumber(formData.mobileDialCode + val)) {
                    setMobileNumberError(PHONE_ERROR);
                  }
                }}
              />
            </Field>
          </div>
        </Section>

        {/* NAME PRONUNCIATION */}
        <Section id="onsite" icon="fa-microphone" title="Name Pronunciation" subtitle="Please click to record and crop as needed" accent={RED}>
          <div style={{ marginBottom: '20px' }}>
            <div className="onsite-form-audio-label">Please record your name here so media will pronounce it correctly</div>
            <OnsiteAudioRecorder
              existingUrl={formData.pronunciationAudioUrl}
              onBlobReady={(blob) => { audioBlobRef.current = blob; }}
              onDeleteUrl={handleDeleteUrl}
            />
          </div>
        </Section>

        {/* DIGITAL SIGNATURE */}
        <Section id="signature" icon="fa-pen-nib" title="Please sign here" subtitle="Tap the box below to sign the on-site agreement" accent={GOLD}>
          <OnsiteSignatureField
            value={formData.signatureDataUrl}
            locked={padReadOnly}
            signerName={`${formData.firstName} ${formData.lastName}`.trim()}
            onChange={handleSignatureChange}
          />
          {sigError && <FieldError>Signature required before registering.</FieldError>}
          <label className="onsite-form-regs">
            <input
              type="checkbox"
              name="regulationsAcknowledged"
              checked={formData.regulationsAcknowledged}
              onChange={(e) => { handleChange(e); if (e.target.checked) setRegulationsError(false); }}
              style={{ width: '22px', height: '22px', accentColor: GREEN, marginTop: '2px', flexShrink: 0, cursor: 'pointer' }}
            />
            <span className="onsite-form-regs__text">
              I have read, understood, and agree to the IGF regulations. By signing above, I acknowledge my acceptance of the terms outlined.
            </span>
          </label>
          {regulationsError && <FieldError>Please acknowledge the regulations before registering.</FieldError>}
        </Section>

        <div className="onsite-form-footer">
          <p className="onsite-form-footer__note">
            Done? Please click on SUBMIT below and see an IGF Staff to validate together with him/her.
          </p>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="onsite-form-footer__btn onsite-form-footer__btn--ready"
          >
            {submitting
              ? <><Spinner animation="border" size="sm" /> Saving…</>
              : <><i className="fa-solid fa-circle-check" /> SUBMIT <i className="fa-solid fa-arrow-right" /></>
            }
          </button>
        </div>
      </div>
      <div onClick={handleCornerTap}
           style={{ position: 'fixed', bottom: 0, left: 0, width: '160px', height: '160px' }}
           onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.006)'}
           onMouseLeave={e => e.currentTarget.style.background = 'transparent'} />
    </div>
  );
};

export default OnsiteRegistrationForm;
